// The demo workspace: the same data as the Mac app's demo (apple/Tally/Services/DemoData.swift), modelled on the
// design system's Today reference (Friday, 14:36). Agent spans come from the real span engine, fed with generated
// events, and the same seeded random numbers, so the Mac and the web show the same numbers. An in-memory server
// answers like the real one; nothing leaves the browser.
import { computeSession, DEFAULT_CONFIG, type EventType, type TallyEvent } from '../../../packages/core/src/index.ts';
import { addDays, dayRange, localParts } from '../lib/calendar.ts';
import { ApiError, type Api, type LiveHandlers } from './client.ts';
import type { NewInvoice, Role, StripeInvoice, StripeStatus, TeamResponse,
  ActivitySpan, AgentSession, Base, Change, Client, Device, Me, Membership, ProStatus, Project, ProjectRule, PushStatus, RangeResponse, RawEvent,
  SessionResponse, Setting, SpanEdit, TimeEntry, TimeEntryVersion, UsageTotal, WorkspacesResponse, WorkspaceSummary, InviteResult,
} from './types.ts';

export const DEMO_WORKSPACE = '5b0c1c2e-7a41-4d6b-9f0e-2c7d1a000001';
export const DEMO_MEMBER = '5b0c1c2e-7a41-4d6b-9f0e-2c7d1a000002';
export const DEMO_MAC = '5b0c1c2e-7a41-4d6b-9f0e-2c7d1a000003';
export const DEMO_SERVER = '5b0c1c2e-7a41-4d6b-9f0e-2c7d1a000004';
const HOUR = 3_600_000;

/** Today at 14:36 local time. */
export function demoNow(real: number, timeZone: string): number {
  return dayRange(real, timeZone).start + 14 * HOUR + 36 * 60_000;
}

/** Swift's `SeededRandom` (64-bit LCG), bit for bit. */
function seeded(seed: bigint) {
  let state = seed;
  return (lo: number, hi: number): number => {
    state = (state * 6364136223846793005n + 1442695040888963407n) & 0xffff_ffff_ffff_ffffn;
    return lo + Number((state >> 33n) % BigInt(hi - lo + 1));
  };
}

type Step = { kind: 'work' | 'waitInput' | 'waitPermission'; from: number; to: number };
const work = (from: number, to: number): Step => ({ kind: 'work', from, to });
const waitInput = (from: number, to: number): Step => ({ kind: 'waitInput', from, to });
const waitPermission = (from: number, to: number): Step => ({ kind: 'waitPermission', from, to });

interface DemoSession {
  provider: string;
  id: string;
  repo: string;
  branch: string;
  project: string | null;
  plan: Step[];
  parent?: string;
  coverage?: 'full' | 'partial';
  toolEvery?: number;
}

export interface DemoData {
  base: Base;
  entries: TimeEntry[];
  versions: TimeEntryVersion[];
  sessions: AgentSession[];
  spans: ActivitySpan[];
  edits: SpanEdit[];
  usage: UsageTotal[];
  events: Map<string, RawEvent[]>;
  devices: Device[];
}

export function seedDemo(now: number, timeZone: string): DemoData {
  const dayStart = dayRange(now, timeZone).start;
  const at = (hours: number, dayOffset = 0) => dayStart + Math.trunc((hours + dayOffset * 24) * HOUR);
  const isWorkday = (offset: number) => {
    const weekday = localParts(addDays(dayStart, offset, timeZone), timeZone).weekday;
    return weekday !== 6 && weekday !== 7;
  };
  const client = (id: string, name: string, rate: number | null): Client =>
    ({ id, name, defaultRateCents: rate, currency: 'EUR', createdAt: at(-72), updatedAt: at(-72), deletedAt: null });
  const clients = [client('c1', 'Nordlicht GmbH', 9500), client('c2', 'Berger & Söhne', 11000), client('c3', 'Café Kranz', 8000), client('c7', 'Intern', null)];
  const project = (id: string, clientId: string, name: string, colorSlot: number, rateCents: number | null, created: number, extra: Partial<Project> = {}): Project => ({
    id, clientId, name, colorSlot, rateCents, currency: 'EUR', billable: true, roundingMinutes: 0, archivedAt: null, createdAt: at(created), updatedAt: at(created),
    deletedAt: null, ...extra,
  });
  const projects = [
    project('p1', 'c1', 'Kundenportal Relaunch', 1, 9500, -72),
    project('p2', 'c2', 'API Gateway', 2, 11000, -71),
    project('p3', 'c3', 'Website Wartung', 3, 8000, -70, { roundingMinutes: 15 }),
    project('p7', 'c7', 'Tellary App', 7, null, -69, { billable: false }),
  ];
  // Folder rules stay on the Mac; the server (and so the web) knows the remote rule only.
  const rules: ProjectRule[] = [
    { id: 'r-p2-remote', projectId: 'p2', kind: 'remote', pattern: 'github.com/berger/api-gateway', priority: 0, createdAt: at(-71), updatedAt: at(-71), deletedAt: null },
  ];

  // Your time today (the last entry is running since 13:12).
  const entry = (id: string, from: number, to: number | null, projectId: string, text: string, tags: string[], dayOffset = 0): TimeEntry => ({
    id, memberId: DEMO_MEMBER, projectId, description: text, startAt: at(from, dayOffset), endAt: to === null ? null : at(to, dayOffset), timeZone,
    billable: projectId !== 'p7', source: 'human', version: 1, tags, createdAt: at(from, dayOffset), updatedAt: at(from, dayOffset), deletedAt: null,
  });
  const entries: TimeEntry[] = [
    entry('e-today-1', 8.3, 10.1, 'p7', 'Planung Sprint 14', ['planung']),
    entry('e-today-2', 10.5, 12.2, 'p1', 'Kundencall Nordlicht', ['call', 'nordlicht']),
    entry('e-today-3', 13.2, null, 'p1', 'Checkout-Flow Review mit Team', ['review']),
  ];
  // The call was edited once: it had a shorter name and ended at 12:00.
  const call = entries[1] as TimeEntry;
  const versions: TimeEntryVersion[] = [{
    id: 'v-today-2', entryId: call.id, version: 1, editedAt: at(12.3),
    snapshot: { ...call, workspaceId: DEMO_WORKSPACE, description: 'Call Nordlicht', endAt: at(12.0), tags: ['call'] },
  }];
  call.version = 2;
  call.updatedAt = at(12.3);

  // Eight weeks of history: workdays with a few entries each.
  const texts: Record<string, string[]> = {
    p1: ['Umsetzung Login', 'Checkout-Flow', 'Abstimmung Design', 'Bugfixes Kundenportal'],
    p2: ['Review Rate-Limiter', 'Redis-Anbindung', 'Lasttests Gateway'],
    p3: ['Updates Website', 'Footer und Impressum', 'Plugin-Updates'],
    p7: ['Tellary: Collector', 'Tellary: Berichte', 'Sprintplanung'],
  };
  let rng = seeded(42n);
  let historyId = 0;
  for (let day = 1; day <= 56; day++) {
    if (!isWorkday(-day)) continue;
    let hour = 8.5 + rng(0, 4) * 0.25;
    const count = rng(2, 4);
    for (let i = 0; i < count; i++) {
      const p = (['p1', 'p1', 'p2', 'p3', 'p7'] as const)[rng(0, 4)] as string;
      const length = 0.75 + rng(0, 10) * 0.25;
      const list = texts[p] ?? [];
      const tags = p === 'p1' && rng(0, 3) === 0 ? ['review'] : [];
      const text = list[rng(0, list.length - 1)] ?? '';
      entries.push(entry(`e-hist-${++historyId}`, hour, Math.min(18.5, hour + length), p, text, tags, -day));
      hour = Math.min(18.0, hour + length + rng(0, 3) * 0.25);
    }
  }
  const settings: Setting[] = [
    { key: 'invoiceSender', value: 'Marcel Hübner\nPDR-Team GmbH\nMusterstraße 1 · 10115 Berlin', updatedAt: at(-72) },
    { key: 'vatPercent', value: '19', updatedAt: at(-72) },
    { key: 'tokenCosts', value: '{"markup":0,"usdRate":9000}', updatedAt: at(-72) },
  ];

  // Agent sessions as event streams → spans via the span engine.
  const sessions: AgentSession[] = [];
  const spans: ActivitySpan[] = [];
  const events = new Map<string, RawEvent[]>();
  const write = (s: DemoSession, sessionDayStart: number) => {
    const t = (h: number) => Math.min(now, sessionDayStart + Math.trunc(h * HOUR));
    const list: TallyEvent[] = [];
    let n = 0;
    const add = (type: EventType, instant: number, tool?: string) => {
      n += 1;
      const e: TallyEvent = { id: `${s.id}-${n}`, at: instant, provider: s.provider, session: s.id, type, source: 'hook' };
      if (tool) e.toolUseId = tool;
      list.push(e);
    };
    let pendingTool: string | null = null;
    const every = (s.toolEvery ?? 50) * 1000;
    for (const step of s.plan) {
      if (step.kind === 'work') {
        if (pendingTool) {
          add('tool_finished', t(step.from), pendingTool);
          pendingTool = null;
        } else add('prompt_submitted', t(step.from));
        let cursor = t(step.from) + 20_000;
        let k = 0;
        while (cursor + every - 5_000 < t(step.to)) {
          k += 1;
          add('tool_started', cursor, `${s.id}-t${n}-${k}`);
          add('tool_finished', cursor + every - 15_000, `${s.id}-t${n}-${k}`);
          cursor += every;
        }
        if (t(step.to) >= now) add('heartbeat', now - 5_000);
      } else if (step.kind === 'waitInput') {
        add('turn_completed', t(step.from));
      } else {
        const tool = `${s.id}-perm-${n}`;
        add('tool_started', t(step.from) - 1_000, tool);
        add('permission_requested', t(step.from), tool);
        pendingTool = tool;
      }
    }
    const last = s.plan[s.plan.length - 1];
    if (last?.kind === 'work' && t(last.to) < now) add('turn_completed', t(last.to));
    const result = computeSession(list, now, DEFAULT_CONFIG);
    const id = `session-${s.id}`;
    const first = list[0]?.at ?? now;
    sessions.push({
      id, provider: s.provider, providerSessionId: s.id, deviceId: DEMO_MAC, method: 'hooks', parentId: s.parent ? `session-${s.parent}` : null,
      repoName: s.repo, gitRemote: null, gitBranch: s.branch, title: `${s.repo} · ${s.branch}`, titleSource: 'default',
      model: s.provider === 'codex' ? 'gpt-5.5-codex' : s.provider === 'claude-code' ? 'claude-opus-5-5' : null, originator: null,
      projectId: s.project, projectSource: s.project ? 'rule' : null, state: result.state, stateSince: result.stateSince,
      lastEventAt: result.lastEventAt, lastActivityAt: result.lastActivityAt, countedMs: result.totals.countedMs,
      waitingMs: result.totals.waitingInputMs + result.totals.waitingPermissionMs, startedAt: first, endedAt: result.state === 'ended' ? result.stateSince : null,
      coverage: s.coverage ?? 'full', editedAt: null, createdAt: first, updatedAt: result.lastEventAt ?? first, deletedAt: null,
    });
    for (const span of result.spans) {
      spans.push({ id: `${id}:${span.state}:${span.start}`, sessionId: id, state: span.state, reason: span.reason, startAt: span.start, endAt: span.end,
        eventIds: span.eventIds, updatedAt: now, deletedAt: null });
    }
    events.set(id, list.map((e, i) => ({ id: e.id, type: e.type, at: e.at, seq: i, source: e.source ?? 'hook', tool_use_id: e.toolUseId ?? null, reason: null,
      provider_event: null })));
  };
  const today: DemoSession[] = [
    { provider: 'claude-code', id: 'demo-cc-portal', repo: 'portal', branch: 'feat/auth-refresh', project: 'p1',
      plan: [work(9.0, 9.8), waitInput(9.8, 10.3), work(10.3, 11.6), work(13.4, 13.75), waitPermission(13.75, 13.9), work(13.9, 14.6)] },
    { provider: 'codex', id: 'demo-cx-gateway', repo: 'api-gateway', branch: 'perf/redis-limiter', project: 'p2',
      plan: [work(11.0, 12.6), waitInput(12.6, 13.5), work(13.5, 14.3), waitPermission(14.3, 14.6)] },
    { provider: 'claude-code', id: 'demo-cc-website', repo: 'website', branch: 'fix/footer-links', project: 'p3',
      plan: [work(8.6, 9.1), work(12.7, 13.1), waitInput(13.1, 13.3), work(14.4, 14.6)] },
    { provider: 'claude-code', id: 'demo-cc-spike', repo: 'experiments', branch: 'main', project: null, plan: [work(11.9, 12.3)] },
    { provider: 'codex', id: 'demo-cx-gateway-sub', repo: 'api-gateway', branch: 'perf/redis-limiter', project: 'p2', plan: [work(11.4, 11.9)], parent: 'demo-cx-gateway' },
    { provider: 'cursor', id: 'demo-cu-website', repo: 'website', branch: 'fix/footer-links', project: 'p3', plan: [work(9.3, 9.7), waitInput(9.7, 9.8)], coverage: 'partial' },
  ];
  for (const s of today) write(s, dayStart);

  // History for reports and project pages: agents on most workdays, overlapping your time now and then.
  rng = seeded(7n);
  const kinds: [string, string | null, string, string][] = [
    ['claude-code', 'p1', 'portal', 'feat/checkout'], ['codex', 'p2', 'api-gateway', 'perf/cache'], ['claude-code', 'p3', 'website', 'chore/deps'],
    ['codex', 'p7', 'tellary', 'feat/reports'], ['cursor', 'p3', 'website', 'fix/forms'],
    ['gemini-cli', 'p2', 'api-gateway', 'feat/rate-limits'], ['github-copilot', 'p1', 'portal', 'fix/a11y'],
  ];
  for (let day = 1; day <= 56; day++) {
    if (!isWorkday(-day)) continue;
    const start = dayStart - day * 86_400_000;
    const count = rng(1, 3);
    for (let n = 0; n < count; n++) {
      const [provider, p, repo, branch] = kinds[rng(0, 6)] as [string, string | null, string, string];
      const from = 9.0 + rng(0, 14) * 0.5;
      const w = 0.5 + rng(0, 6) * 0.25;
      const wait = 0.25 + rng(0, 3) * 0.25;
      write({ provider, id: `hist-${day}-${n}`, repo, branch, project: p, coverage: provider === 'cursor' || provider === 'github-copilot' ? 'partial' : 'full', toolEvery: 90,
        plan: [work(from, from + w), waitInput(from + w, from + w + wait), work(from + w + wait, from + 2 * w + wait)] }, start);
    }
  }

  // Corrections: part of the portal session was research for Tally; a discard was taken back.
  const edits: SpanEdit[] = [
    { id: 'edit-1', sessionId: 'session-demo-cc-portal', kind: 'reassign', startAt: at(11.25), endAt: at(11.6), projectId: 'p7', note: 'Recherche für Tellary',
      createdAt: at(12.4), createdBy: 'Marcel Hübner', revertedAt: null, updatedAt: at(12.4) },
    { id: 'edit-2', sessionId: 'session-demo-cc-portal', kind: 'discard', startAt: at(9.55), endAt: at(9.8), projectId: null, note: 'Versehentlich gestartet',
      createdAt: at(12.45), createdBy: 'Marcel Hübner', revertedAt: at(12.5), updatedAt: at(12.5) },
  ];
  // Token numbers (opt-in on the Mac): Claude Code via OpenTelemetry, Codex from its logs.
  const u = (sessionId: string, source: string, kind: UsageTotal['kind'], value: number): UsageTotal =>
    ({ sessionId, model: source === 'otel' ? 'claude-opus-5-5' : '', kind, epoch: 0, value, source, updatedAt: now });
  const usage = [
    u('session-demo-cc-portal', 'otel', 'input', 182_400), u('session-demo-cc-portal', 'otel', 'output', 46_900),
    u('session-demo-cc-portal', 'otel', 'cache_read', 2_410_000), u('session-demo-cc-portal', 'otel', 'cache_write', 312_000),
    u('session-demo-cc-portal', 'otel', 'cost_microusd', 4_870_000), u('session-demo-cc-portal', 'otel', 'active_cli_ms', 9_720_000),
    u('session-demo-cx-gateway', 'log', 'input', 96_200), u('session-demo-cx-gateway', 'log', 'cache_read', 1_380_000),
    u('session-demo-cx-gateway', 'log', 'output', 28_400), u('session-demo-cx-gateway', 'log', 'reasoning', 11_900),
    u('session-demo-cc-website', 'otel', 'input', 21_300), u('session-demo-cc-website', 'otel', 'output', 6_100),
    u('session-demo-cc-website', 'otel', 'cache_read', 402_000), u('session-demo-cc-website', 'otel', 'cost_microusd', 710_000),
  ];
  const iso = (t: number) => new Date(t).toISOString();
  return {
    base: { seq: 1, projects, clients, projectRules: rules, settings, members: [{ id: DEMO_MEMBER, name: 'Marcel Hübner' }] },
    entries, versions, sessions, spans, edits, usage, events,
    devices: [
      { id: DEMO_MAC, name: 'MacBook Pro', platform: 'macOS', created_at: iso(at(-72)), last_seen_at: iso(now - 60_000) },
      { id: DEMO_SERVER, name: 'Build-Server', platform: 'linux', created_at: iso(at(-24)), last_seen_at: iso(at(-2)) },
    ],
  };
}

type Clock = () => number;

/** Answers like the server, from memory. Changes apply at once and reach live listeners like a server echo. */
export class DemoApi implements Api {
  readonly demo: boolean = true;
  readonly guest: boolean = false;
  protected data: DemoData;
  protected readonly clock: Clock;
  protected seq = 1;
  private signedIn: boolean;
  private readonly listeners = new Set<LiveHandlers>();
  private activeWorkspaceId = DEMO_WORKSPACE;
  private workspaceList: WorkspaceSummary[] = [{ id: DEMO_WORKSPACE, name: 'Tellary', memberId: DEMO_MEMBER, memberName: 'Marcel Hübner', role: 'owner', personal: false }];
  private readonly workspaceData = new Map<string, { data: DemoData; invites: TeamResponse['invites'] }>();

  private readonly unreachable: boolean;

  /** `empty`: a new workspace without any time; `unreachable`: the server does not answer (error states). */
  /** `?free`: the demo as a Free account sees it (Pro features locked). */
  private free: boolean;

  constructor(clock: Clock, timeZone: string, options: { signedIn?: boolean; empty?: boolean; unreachable?: boolean; free?: boolean } = {}) {
    this.clock = clock;
    this.data = seedDemo(clock(), timeZone);
    if (options.empty) {
      this.data = {
        ...this.data, entries: [], versions: [], sessions: [], spans: [], edits: [], usage: [], events: new Map(),
        base: { ...this.data.base, projects: [], clients: [], projectRules: [], settings: [] },
      };
    }
    this.signedIn = options.signedIn ?? true;
    this.unreachable = options.unreachable ?? false;
    this.free = options.free ?? false;
  }

  /** After every accepted change (the guest mode saves here). */
  protected saved(): void {}

  protected async later<T>(value: T): Promise<T> {
    await new Promise((resolve) => setTimeout(resolve, 0));
    return value;
  }

  async me(): Promise<Me | null> {
    if (this.unreachable) throw new ApiError(0, 'offline');
    if (!this.signedIn) return this.later(null);
    return this.later({
      user: { id: 'demo-user', email: 'marcel@example.de', name: 'Marcel Hübner' },
      workspace: this.workspaceList.find((w) => w.id === this.activeWorkspaceId)!,
      devices: this.data.devices,
      pro: this.free ? { active: false, source: null, until: null } : { active: true, source: 'code', until: null },
    });
  }

  async startEmail(): Promise<void> { await this.later(undefined); }
  async verifyEmail(): Promise<{ created: boolean; workspace: Membership | null }> {
    this.signedIn = true;
    return this.later({ created: false, workspace: { workspaceId: DEMO_WORKSPACE, memberId: DEMO_MEMBER, role: 'owner' } });
  }
  verifyLink() { return this.verifyEmail(); }
  async logout(): Promise<void> { this.signedIn = false; }
  async claimWorkspace(): Promise<Membership> { return { workspaceId: DEMO_WORKSPACE, memberId: DEMO_MEMBER, role: 'owner' }; }

  setWorkspace(_id: string | null): void {}
  async workspaces(): Promise<WorkspacesResponse> {
    if (!this.workspaceList.some((w) => w.personal)) {
      const created = await this.createWorkspace('Privat');
      this.workspaceList = this.workspaceList.map((w) => w.id === created.id ? { ...w, personal: true } : w);
    }
    return { workspaces: this.workspaceList, activeWorkspaceId: this.activeWorkspaceId, pendingInvites: [] };
  }
  async createWorkspace(name: string): Promise<WorkspaceSummary> {
    const workspaceId = crypto.randomUUID(), memberId = crypto.randomUUID();
    this.workspaceList = [...this.workspaceList, { id: workspaceId, name, memberId, memberName: 'Marcel Hübner', role: 'owner', personal: false }];
    this.workspaceData.set(workspaceId, { data: {
      ...this.data, entries: [], versions: [], sessions: [], spans: [], edits: [], usage: [], events: new Map(), devices: [],
      base: { seq: 1, projects: [], clients: [], projectRules: [], settings: [], members: [{ id: memberId, name: 'Marcel Hübner', role: 'owner' }], you: { memberId, role: 'owner' } },
    }, invites: [] });
    return this.workspaceList.find((w) => w.id === workspaceId)!;
  }
  async selectWorkspace(workspaceId: string): Promise<WorkspaceSummary> {
    const workspace = this.workspaceList.find((w) => w.id === workspaceId);
    if (!workspace) throw new ApiError(403, 'workspace_forbidden');
    if (workspaceId !== this.activeWorkspaceId) {
      this.workspaceData.set(this.activeWorkspaceId, { data: this.data, invites: this.invites });
      const next = this.workspaceData.get(workspaceId)!;
      this.data = next.data; this.invites = next.invites; this.activeWorkspaceId = workspaceId;
      this.stripeInvoices = null;
    }
    return workspace;
  }
  async acceptInvite(_id: string): Promise<WorkspaceSummary> { throw new ApiError(404, 'invite_unavailable'); }
  async declineInvite(_id: string): Promise<void> { throw new ApiError(404, 'invite_unavailable'); }
  async renameWorkspace(name: string): Promise<void> {
    this.workspaceList = this.workspaceList.map((w) => w.id === this.activeWorkspaceId ? { ...w, name } : w);
  }
  async leaveWorkspace(): Promise<void> { throw new ApiError(409, 'last_owner'); }

  base(): Promise<Base> {
    return this.later({ ...this.data.base, seq: this.seq });
  }

  range(start: number, end: number, usage: boolean): Promise<RangeResponse> {
    const d = this.data;
    const overlaps = (s: number, e: number | null) => s < end && (e === null || e > start);
    const ids = new Set(d.spans.filter((s) => s.deletedAt === null && overlaps(s.startAt, s.endAt)).map((s) => s.sessionId));
    for (const s of d.sessions) if (s.state === 'working' || s.state === 'waiting_input' || s.state === 'waiting_permission') ids.add(s.id);
    const sessions = d.sessions.filter((s) => ids.has(s.id) && s.deletedAt === null);
    const known = new Set(sessions.map((s) => s.id));
    return this.later({
      seq: this.seq, range: { start, end },
      entries: d.entries.filter((e) => e.deletedAt === null && overlaps(e.startAt, e.endAt)),
      sessions,
      spans: d.spans.filter((s) => known.has(s.sessionId) && s.deletedAt === null && overlaps(s.startAt, s.endAt)),
      edits: d.edits.filter((e) => known.has(e.sessionId)),
      usage: usage ? d.usage.filter((x) => known.has(x.sessionId)) : [],
    });
  }

  session(id: string): Promise<SessionResponse | null> {
    const d = this.data;
    const session = d.sessions.find((s) => s.id === id && s.deletedAt === null);
    if (!session) return this.later(null);
    const children = d.sessions.filter((s) => s.parentId === id && s.deletedAt === null);
    const ids = new Set([id, ...children.map((c) => c.id)]);
    return this.later({
      seq: this.seq, session, children,
      spans: d.spans.filter((s) => ids.has(s.sessionId) && s.deletedAt === null),
      edits: d.edits.filter((e) => ids.has(e.sessionId)),
      usage: d.usage.filter((u) => ids.has(u.sessionId)),
    });
  }

  events(sessionId: string): Promise<RawEvent[]> {
    return this.later(this.data.events.get(sessionId) ?? []);
  }

  versions(entryId: string): Promise<TimeEntryVersion[]> {
    return this.later(this.data.versions.filter((v) => v.entryId === entryId).sort((a, b) => b.version - a.version));
  }

  tags(): Promise<string[]> {
    const counts = new Map<string, number>();
    for (const e of this.data.entries) if (e.deletedAt === null) for (const tag of e.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    return this.later([...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([tag]) => tag));
  }

  async push(changes: Change[]): Promise<PushStatus[]> {
    const d = this.data;
    const replace = <T>(list: T[], item: T, same: (x: T) => boolean) => {
      const i = list.findIndex(same);
      if (i >= 0) list[i] = item;
      else list.push(item);
    };
    const now = this.clock();
    for (const change of changes) {
      if (change.op === 'delete') {
        switch (change.entity) {
          case 'agentSessions':
            d.sessions = d.sessions.filter((s) => s.id !== change.id);
            d.spans = d.spans.filter((s) => s.sessionId !== change.id);
            d.edits = d.edits.filter((e) => e.sessionId !== change.id);
            d.usage = d.usage.filter((u) => u.sessionId !== change.id);
            break;
          case 'projectRules': d.base.projectRules = d.base.projectRules.filter((r) => r.id !== change.id); break;
          case 'settings': d.base.settings = d.base.settings.filter((s) => s.key !== change.id); break;
          case 'timeEntries': d.entries = d.entries.map((e) => (e.id === change.id ? { ...e, deletedAt: e.deletedAt ?? now } : e)); break;
          case 'projects': d.base.projects = d.base.projects.map((p) => (p.id === change.id ? { ...p, deletedAt: p.deletedAt ?? now } : p)); break;
          default: break;
        }
        continue;
      }
      switch (change.entity) {
        case 'timeEntries': replace(d.entries, change.data, (e) => e.id === change.data.id); break;
        case 'timeEntryVersions': replace(d.versions, change.data, (v) => v.id === change.data.id); break;
        case 'projects': replace(d.base.projects, change.data, (p) => p.id === change.data.id); break;
        case 'clients': replace(d.base.clients, change.data, (c) => c.id === change.data.id); break;
        case 'projectRules': replace(d.base.projectRules, change.data, (r) => r.id === change.data.id); break;
        case 'agentSessions': replace(d.sessions, change.data, (s) => s.id === change.data.id); break;
        case 'spanEdits': replace(d.edits, change.data, (e) => e.id === change.data.id); break;
        case 'settings': replace(d.base.settings, change.data, (s) => s.key === change.data.key); break;
      }
    }
    this.seq += 1;
    const seq = this.seq;
    this.saved();
    setTimeout(() => { for (const l of this.listeners) l.onChange(seq); }, 30);
    return changes.map(() => 'ok');
  }

  async addDevice(name: string, platform: string): Promise<{ id: string; token: string }> {
    const id = crypto.randomUUID();
    this.data.devices.push({ id, name, platform, created_at: new Date(this.clock()).toISOString(), last_seen_at: null });
    return { id, token: 'tly_d_demo-nur-zur-ansicht' };
  }

  async removeDevice(id: string): Promise<void> {
    this.data.devices = this.data.devices.filter((d) => d.id !== id);
  }

  async exportAccount(): Promise<Blob> {
    const d = this.data;
    const payload = { format: 'tally-server-export', demo: true, data: { ...d.base, timeEntries: d.entries, agentSessions: d.sessions } };
    return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  }

  async deleteAccount(): Promise<void> {
    this.signedIn = false;
  }

  /** The demo never talks to a server: a code "unlocks" only its own pretend account. */
  async redeemProCode(): Promise<{ pro: ProStatus }> {
    this.free = false;
    return this.later({ pro: { active: true, source: 'code', until: null } });
  }

  /** The demo is one person; an open invitation shows the owner's side of teams. */
  private invites: TeamResponse['invites'] = [
    { id: 'demo-invite', email: 'lena@example.de', role: 'member', createdAt: '2026-09-24T09:00:00.000Z', expiresAt: '2026-10-08T09:00:00.000Z' },
  ];

  async team(): Promise<TeamResponse> {
    return this.later({
      role: 'owner',
      members: [{ id: DEMO_MEMBER, name: 'Marcel Hübner', email: 'marcel@example.de', role: 'owner', devices: this.data.devices.length, you: true }],
      invites: this.invites,
    });
  }

  async invite(email: string, role: Role): Promise<InviteResult> {
    const now = this.clock();
    const id = crypto.randomUUID();
    const shareUrl = `${globalThis.location?.origin ?? 'https://example.invalid'}/?invite=${id}`;
    this.invites = [...this.invites.filter((i) => i.email !== email), {
      id, email, role, shareUrl, createdAt: new Date(now).toISOString(), expiresAt: new Date(now + 14 * 86_400_000).toISOString(),
    }];
    return { shareUrl, emailDelivery: 'manual' };
  }

  async revokeInvite(id: string): Promise<void> {
    this.invites = this.invites.filter((i) => i.id !== id);
  }

  async setRole(): Promise<void> {
    throw new ApiError(409, 'last_owner');
  }

  async removeMember(): Promise<void> {
    throw new ApiError(409, 'cannot_remove_yourself');
  }

  /** Stripe in test mode, with last month's invoice paid. Nothing links out: demo invoices exist nowhere else. */
  private stripeStatus: StripeStatus = { available: true, connected: true, keyHint: 'sk_test_…4242', livemode: false, accountName: 'PDR-Team GmbH', webhook: true };
  private stripeInvoices: StripeInvoice[] | null = null;

  private demoInvoices(): StripeInvoice[] {
    if (this.stripeInvoices) return this.stripeInvoices;
    const now = this.clock();
    const august = { start: Date.UTC(2026, 7, 1) - 2 * HOUR, end: Date.UTC(2026, 8, 1) - 2 * HOUR };
    const created = new Date(now - 24 * 86_400_000).toISOString();
    this.stripeInvoices = [{
      id: 'demo-invoice-08', clientId: 'c1', clientName: 'Nordlicht GmbH', number: 'RE-2026-041', rangeStart: august.start, rangeEnd: august.end,
      currency: 'EUR', netCents: 1_396_500, vatBasisPoints: 1900, vatCents: 265_335, grossCents: 1_661_835, stripeTotalCents: 1_661_835,
      lines: [{ kind: 'time', projectId: 'p1', description: 'Kundenportal Relaunch · 147,00 h × 95,00 €', amountCents: 1_396_500 }],
      status: 'paid', stripeInvoiceId: 'in_demo_041', hostedUrl: null, pdfUrl: null, dashboardUrl: null, livemode: false, createdAt: created, updatedAt: created,
    }];
    return this.stripeInvoices;
  }

  async stripe(): Promise<StripeStatus> {
    return this.later(this.stripeStatus);
  }

  async connectStripe(secretKey: string): Promise<StripeStatus> {
    if (!/^(sk|rk)_(live|test)_\w{8,}$/.test(secretKey)) throw new ApiError(400, 'stripe_key_invalid');
    this.stripeStatus = { available: true, connected: true, keyHint: `${secretKey.slice(0, secretKey.indexOf('_', 3) + 1)}…${secretKey.slice(-4)}`,
      livemode: secretKey.includes('_live_'), accountName: 'PDR-Team GmbH', webhook: true };
    return this.later(this.stripeStatus);
  }

  async disconnectStripe(): Promise<void> {
    this.stripeStatus = { available: true, connected: false };
  }

  async invoices(): Promise<StripeInvoice[]> {
    return this.later(this.demoInvoices());
  }

  async createInvoice(input: NewInvoice): Promise<StripeInvoice> {
    if (!this.stripeStatus.connected) throw new ApiError(409, 'stripe_not_connected');
    const existing = this.demoInvoices().find((i) => i.id === input.id);
    if (existing) return existing;
    const net = input.lines.reduce((sum, line) => sum + line.amountCents, 0);
    const bp = input.exempt ? 0 : input.vatBasisPoints;
    const vat = bp > 0 && net > 0 ? Math.floor((net * bp + 5000) / 10_000) : 0;
    const at = new Date(this.clock()).toISOString();
    const invoice: StripeInvoice = {
      id: input.id, clientId: input.clientId, clientName: input.clientName, number: input.number ?? (input.finalize ? 'RE-2026-042' : null),
      rangeStart: input.rangeStart, rangeEnd: input.rangeEnd, currency: input.currency, netCents: net, vatBasisPoints: bp, vatCents: vat,
      grossCents: net + vat, stripeTotalCents: net + vat, lines: input.lines, status: input.finalize ? 'open' : 'draft',
      stripeInvoiceId: `in_demo_${input.id.slice(0, 8)}`, hostedUrl: null, pdfUrl: null, dashboardUrl: null, livemode: false, createdAt: at, updatedAt: at,
    };
    this.stripeInvoices = [invoice, ...this.demoInvoices()];
    return this.later(invoice);
  }

  async refreshInvoice(id: string): Promise<StripeInvoice> {
    const invoice = this.demoInvoices().find((i) => i.id === id);
    if (!invoice) throw new ApiError(404, 'not_found');
    return this.later(invoice);
  }

  live(handlers: LiveHandlers): () => void {
    this.listeners.add(handlers);
    setTimeout(() => handlers.onConnected(true), 0);
    return () => this.listeners.delete(handlers);
  }
}
